import {Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {ReactiveFormsModule} from "@angular/forms";
import {UploadService} from "./upload.service";
import {UploadAssetComponent} from "./upload-asset/upload-asset.component";
import {NgClass} from "@angular/common";
import {UploadAsset} from "./upload-asset/upload-asset";
import {AssetUploadProperty} from "./upload.model";
import {UploadMasterPropertiesComponent} from "./upload-master-properties/upload-master-properties.component";
import {MatButton, MatIconButton} from "@angular/material/button";
import {AssetStateService} from "../assets/asset.state.service";
import {Asset, AssetProperties, AssetProperty, AssetWithProperties} from "../assets/asset.model";
import {UploadStateService} from "./upload-state.service";
import {MatIcon} from '@angular/material/icon';
import {TranslatePipe} from '@ngx-translate/core';
import {forkJoin} from "rxjs";
import {AssetService} from "../assets/asset.service";
import {FullHeighDirective} from '../common/full-heigh.directive';
import {UploadProcess} from "./upload-process";
import {SnackBarService} from '../common/snack-bar-service';
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {getTitle} from "./upload-utils";
import {buildAssetProperties} from "../assets/asset-utils";
import {CollectionService} from "../collection/collection.service";

@Component({
    selector: 'app-upload',
    imports: [ReactiveFormsModule, UploadAssetComponent, UploadMasterPropertiesComponent, MatButton, NgClass, MatIcon, MatIconButton, TranslatePipe, FullHeighDirective, MatProgressSpinner],
    templateUrl: './upload.component.html',
    styleUrl: './upload.component.scss'
})
export class UploadComponent implements OnInit {

    @ViewChild('filesInput') fileInput: ElementRef<HTMLInputElement> | undefined;
    @ViewChild('masterProperties') masterPropertiesComponent!: UploadMasterPropertiesComponent;
    @ViewChildren('uploadAsset') uploadAssetComponents!: QueryList<UploadAssetComponent>;

    uploadProperties: AssetUploadProperty[] = []; // upload properties from database
    uploadAssets: UploadAsset[] = [];
    loading = false;
    canSubmit = false;
    submitting = false;
    makeTitlesUnique = false;

    constructor(
        private uploadService: UploadService,
        private assetStateService: AssetStateService,
        private uploadStateService: UploadStateService,
        private assetService: AssetService,
        private collectionService: CollectionService,
        private snack: SnackBarService
    ) {
    }
    @HostListener('document:keydown.escape', ['$event'])
    onEsc(ev: KeyboardEvent) {
        this.close();
    }

    get active(): boolean {
        return this.uploadStateService.mode !== 'inactive';
    }

    get edit(): boolean {
        return this.uploadStateService.mode === 'edit';
    }

    close() {
        this.uploadStateService.mode = 'inactive';
    }

    ngOnInit(): void {
        this.uploadStateService.mode$.subscribe({
            next: (mode) => {
                if (mode === 'inactive') {
                    this.reset();
                } else {
                    this.loadData();
                }
            }
        })
    }

    private reset() {
        this.canSubmit = false;
        this.uploadAssets = [];
        this.uploadProperties = [];
        if (this.masterPropertiesComponent) {
            this.updateMasterProperties();
        }
    }

    private loadData() {
        this.loading = true;
        this.uploadService.getUploadProperties().subscribe({
            next: (properties) => {
                this.uploadProperties = properties;
                for (const property of properties) {
                    if (property.validation?.required) {
                        this.uploadStateService.addVisibleProperty(property.name);
                    }
                }
                if (this.edit) {
                    this.loadAssetsForEditing(properties);
                }
                // timeout for UX and because the native element is not available yet in on init
                setTimeout(() => {
                    this.fileInput?.nativeElement.click();
                }, 500);
            },
            error: (error) => {
                console.error('error loading upload properties', error);
                this.snack.showMessage({
                    message: 'error.unknown',
                    success: false
                });
                this.uploadStateService.mode = 'inactive';
            },
            complete: () => this.loading = false
        });
    }

    private loadAssetsForEditing(uploadProperties: AssetUploadProperty[]) {
        let assets = this.assetStateService.assetSelection;
        if (assets.length === 0) {
            console.warn('no assets selected');
        } else {
            this.loading = true;
            // console.log('loading assets', assets.length);
            // TODO this could be done on the backend?
            let fullAssets$ = forkJoin(assets.map(a => this.assetService.getAsset(a.uuid)));
            fullAssets$.subscribe({
                next: (assets) => {
                    this.uploadAssets = assets.map(asset => this.buildUploadAsset(asset, uploadProperties));
                    this.canSubmit = this.calculateCanSubmit();
                    this.updateMasterProperties();
                },
                error: (error) => {
                    console.error('error loading assets', error);
                    this.snack.showMessage({
                        message: 'error.unknown',
                        success: false
                    });
                    this.uploadStateService.mode = 'inactive';
                },
                complete: () => this.loading = false
            })
        }
    }

    private buildUploadAsset(asset: AssetWithProperties, properties: AssetUploadProperty[]): UploadAsset {
        let props = buildAssetProperties(properties, asset);
        return {
            properties: props,
            selected: true,
            assetId: asset.uuid,
            fileId: asset.assetBinary.uuid,
            thumbnail: asset.thumbnails.sm,
            fileExtension: asset.assetBinary.fileExtension,
            assetBinary: asset.assetBinary,
            valid: true,
            completed: true
        };
    }


    handleFilesInput($event: Event) {
        let target = $event.target as HTMLInputElement;
        let files = target.files;
        if (files === null) {
            console.warn('no files in event', $event);
            return;
        }
        for (const file of files) {
            if (file === null) {
                console.warn('file is null');
                continue;
            }
            this.handleNewFile(file);
        }
    }

    removeUpload(index: number) {
        this.uploadAssets.splice(index, 1);
        this.canSubmit = this.calculateCanSubmit();
        this.updateMasterProperties();
    }

    handleDrop($event: DragEvent) {
        $event.preventDefault();
        let dataTransfer = $event.dataTransfer;
        if (!dataTransfer) {
            return;
        }
        const files = dataTransfer.files;
       // console.log('dropped', files.length, 'files');
        for (let i = 0; i < files.length; i++) {
            let dropFile = files.item(i);
            if (dropFile === null) {
                console.warn('file at index', i, 'is null');
                continue;
            }
            this.handleNewFile(dropFile);
        }
    }

    private handleNewFile(theFile: File) {
        let uploadFile: UploadAsset = this.buildEmptyUpload(getTitle(theFile), theFile);
        this.uploadAssets.push(uploadFile);
        this.updateMasterProperties();
        this.performUpload(uploadFile);
    }

    private buildEmptyUpload(title: string, file: File): UploadAsset {
        return {
            properties: {
                title
            },
            selected: true,
            completed: false,
            valid: false,
            file: file
        };
    }

    handleDragOver($event: DragEvent) {
        $event.preventDefault();
    }

    fileChanged(file: File | null, i: number) {
       // console.log('file changed', file, i);
        const uploadAsset = this.uploadAssets[i];
        if (file === null) {
            console.warn('file is null');
            this.removeUpload(i);
        } else {
            uploadAsset.file = file;
            if (!this.edit) {
                uploadAsset.properties["title"] = getTitle(file);
            }
            this.performUpload(uploadAsset);
        }
    }

    performUpload(uploadAsset: UploadAsset) {
        uploadAsset.completed = false;
        this.canSubmit = false;
        let fileRef = uploadAsset.file;
        if (!fileRef) {
            console.warn('file is null');
            return;
        }

        let uploadProcess = new UploadProcess(this.uploadService, fileRef, 1024 * 1024); // 1MB
        uploadProcess.$progress.subscribe({
            next: (progress) => {
                uploadAsset.progress = progress;
            }
        });
        uploadProcess.$result.subscribe(
            (response) => {
                if (!response) {
                    console.warn('upload response is null');
                } else {
                  //  console.log('upload success', response);
                    uploadAsset.fileId = response.uuid;
                    uploadAsset.thumbnail = response.thumbnails?.sm;
                    uploadAsset.completed = !!response.isComplete;
                    this.canSubmit = this.calculateCanSubmit();
                }
            },
            (error) => {
                console.error('upload error', error);
                this.snack.showMessage({
                    message: 'upload.file.failure',
                    success: false
                });
            }
        );

       // console.log('starting upload process');
        uploadProcess.start();
    }

    submit() {
        if (!this.canSubmit) {
            console.warn('cannot submit');
            return;
        }
        this.submitting = true;

        if (!this.edit) {
            this.doCommitFiles(this.uploadAssets);
        } else {
            this.doCommitEdit(this.uploadAssets);
        }
    }

    private doCommitFiles(files: UploadAsset[]) {
        this.uploadService.commitUpload(
            files.map(f => {
                if (!f.fileId) {
                    throw new Error('file id is missing'); // should never happen
                }
                return {
                    tempBinary: {uuid: f.fileId},
                    ...f.properties
                }
            })
        ).subscribe({
            next: (response) => {
                this.snack.showMessage({
                    message: 'upload.success',
                    success: true
                });
                this.handleCollectionAssignment(response)
                this.submitting = false;
                this.uploadStateService.mode = 'inactive';
            },
            error: (error) => {
                this.snack.showMessage({
                    message: 'upload.failure',
                    success: false
                });
                this.submitting = false;
            }
        });
    }

    private doCommitEdit(files: UploadAsset[]) {
        this.uploadService.updateUpload(
            files.map(f => {
                if (!f.assetId || !f.assetBinary) {
                    throw new Error('asset cannot be updated'); // should never happen
                }
                if (f.fileId) {
                    return {
                        uuid: f.assetId,
                        tempBinary: {uuid: f.fileId},
                        ...f.properties
                    }
                }
                return {
                    uuid: f.assetId,
                    ...f.properties
                }
            })
        ).subscribe({
            next: (updated) => {
                this.snack.showMessage({
                    message: 'upload.success',
                    success: true
                });

                this.handleCollectionAssignment(updated)

                let assets = this.assetStateService.loadedAssets;
                // use the returned assets to update the local state, that way we preserve the scroll state
                for (let i = 0; i < updated.length; i++) {
                    let updatedAsset = updated[i];
                    let index = assets.findIndex(a => a.uuid === updatedAsset.uuid);
                    if (index > -1) {
                        assets[index] = updatedAsset;
                    }
                }
                this.submitting = false;
                this.assetStateService.clearAssetSelection();
                this.uploadStateService.mode = 'inactive';
            },
            error: (error) => {
                this.submitting = false;
                console.error('upload failed', error);
                this.snack.showMessage({
                    message: 'upload.failure',
                    success: false
                });
            }
        });
    }

    private handleCollectionAssignment(assets: Asset[]) {
        let uploadCollection = this.masterPropertiesComponent.getUploadCollection();
        let assetIds = assets.map(a => a.uuid);
        if (uploadCollection.uuid) {
           // console.log('assigning to existing collection ', uploadCollection.uuid);
            this.collectionService.addAssetsToCollection(uploadCollection.uuid, assetIds).subscribe({
                next: () => {
                    console.log('added to collection'); // do not show message here, it is already as "upload success"
                },
                error: (error) => {
                    console.error('error adding to collection', error);
                    this.snack.showMessage({
                        message: 'upload.collection.error',
                        success: false
                    });
                }
            });
        } else if (uploadCollection.name) {
            console.log('creating new collection ', uploadCollection.name);
            this.collectionService.createCollection({
                name: uploadCollection.name,
                description: '',
                assets: assetIds
            }).subscribe({
                next: (col) => {
                    console.log('created collection'); // do not show message here, it is already as "upload success"
                },
                error: (error) => {
                    console.error('error creating collection', error);
                    this.snack.showMessage({
                        message: 'upload.collection.error',
                        success: false
                    });
                }
            });
        }
    }

    uploadAssetPropertiesChanged($event: AssetProperties, index: number) {
        this.uploadAssets[index].properties = $event;
        this.updateMasterProperties();
    }

    private updateMasterProperties() {
        let allAssetProperties = this.uploadAssets.map(f => f.properties);
        let cleanedProperties = allAssetProperties.map(value => cleanProperties(value));
        this.masterPropertiesComponent.disabled = this.uploadAssets.length === 0;
        this.masterPropertiesComponent.assetPropertiesChanged(cleanedProperties);
    }

    masterPropertyChanged($event: AssetProperty) {
        let usedIndex = 0;
        let propertyKey = $event.key;
        if ($event.value && !this.uploadStateService.visibleProperties.includes(propertyKey)){
            this.uploadStateService.addVisibleProperty(propertyKey);
        }
        this.uploadAssetComponents.forEach((uploadFileComponent, index) => {
            if (uploadFileComponent.selected) {
                usedIndex++;
                if (this.makeTitlesUnique && usedIndex > 1 && propertyKey === 'title') {
                    uploadFileComponent.handleMasterPropertyChanged({...$event, value: $event.value + ' ' + usedIndex}, false);
                } else {
                    uploadFileComponent.handleMasterPropertyChanged($event, this.masterPropertiesComponent.propertiesAdditive);
                }
            }
        });
    }

    selectionChanged($event: boolean, index: number) {
        this.uploadAssets[index].selected = $event;
    }

    checkFormValid() {
        this.canSubmit = this.calculateCanSubmit();
    }

    private calculateCanSubmit() {
        return this.uploadAssets.length > 0 && this.uploadAssets.every(f => f.valid && f.completed);
    }
}

function cleanProperties(value: AssetProperties) {
    let cleanedValue: AssetProperties = {};
    for (let key in value) {
        let val = value[key];
        if (val) {
            cleanedValue[key] = val;
        }
    }
    return cleanedValue;
}
